/**
 * Phase 2 custom domains (PM 01fda321): which hosts can be a club's own
 * domain, and the club the boot resolved for one.
 *
 * On a club's own domain (gymnasticsofyork.com) the host names no slug by
 * itself: the boot asks the backend (GET /public/bootstrap-by-host, active
 * domains only) and records the answer here, so getTenantSlugFromHost,
 * getTenantId's host fallback and the path rewrite treat the custom domain
 * exactly like a phase 1 subdomain. The answer is recorded for ONE host and
 * any other host never sees it.
 *
 * Kept out of hostResolver.ts on purpose: that module hands the hostname to
 * @mediatechgroup/site-host and keeps no host rule of its own
 * (src/test/__tests__/retired.siteHost.test.ts).
 */
import { createSiteHost } from "@mediatechgroup/site-host";
import { SITE_HOST_CONFIG } from "../config/siteHost";

const siteHost = createSiteHost(SITE_HOST_CONFIG);

let resolved: { host: string; slug: string } | null = null;

/** Record (or clear, with null) the club the backend resolved for `host`. */
export const setResolvedCustomDomainSlug = (host: string, slug: string | null): void => {
  resolved = slug && host ? { host: host.toLowerCase(), slug } : null;
};

/** The slug resolved for exactly this host at boot, or null. */
export const resolvedCustomDomainSlugFor = (hostname: string | null | undefined): string | null =>
  resolved && hostname && hostname.toLowerCase() === resolved.host ? resolved.slug : null;

/**
 * True when `hostname` could be a club's own domain. Only these hosts make
 * the boot ask the registry.
 *
 * site-host's couldBeTenantDomain refuses the platform hosts, the staging
 * hosts, anything under a platform suffix (.netlify.app), localhost and IP
 * addresses, each with or without a leading www. With
 * refuseTenantBaseDomains (site-host 0.6.0) it also refuses clubcentral.app
 * and everything under it (phase 1 subdomains and the platform's own
 * labels). With hostname: "strict" (one-letter top label refused since
 * 0.6.1) it accepts only a strict DNS hostname: no port, no trailing dot, no
 * underscore, no label starting or ending with a hyphen, a top label of two
 * or more characters that starts with a letter, the form the registry
 * stores. Both replaced rules Club Central kept locally; the answers are
 * pinned in __tests__/customDomainHost.pinned.test.ts.
 */
export const isCustomDomainHost = (hostname: string | null | undefined): boolean => {
  if (!hostname) return false;
  return siteHost.couldBeTenantDomain(hostname.toLowerCase(), { refuseTenantBaseDomains: true, hostname: "strict" });
};
